import { useEffect, useRef, useState } from 'react';
import { WorksApiService } from 'src/api/works/works';
import { WorkStatus } from 'src/api/works/types';
import container from 'src/config/inversify-config';
import { useLocalStorageState, useMount } from 'ahooks';

export interface Job {
  jobId: string;
  timestamp: number;
  maxAttempts: number;
  interval: number;
  attemptsMade: number;
}

type PollCallback<T> = (result: T) => void | Promise<void>;

export function useJobQueue<T>(storageKey: string, onResult?: PollCallback<T>, OnError?: () => void) {
  const [jobs, setJobs] = useLocalStorageState<Job[]>(storageKey, {
    defaultValue: []
  });

  const [hasRunningJobs, setHasRunningJobs] = useState(false);

  const handlers = useRef(new Map<string, { onResult?: PollCallback<T> }>());
  const worksApi = container.get<WorksApiService>(WorksApiService);

  useMount(() => {
    if (!jobs) return;

    const recentJobs = jobs.filter((j) => j.timestamp > Date.now() - 2 * 60 * 60 * 1000);
    if (recentJobs.length === 0) {
      setHasRunningJobs(false);
    }

    setJobs(recentJobs); // cleanup expired jobs
  });

  const updateAttempts = (jobId: string, attempts: number) => {
    setJobs((prev = []) => prev.map((j) => (j.jobId === jobId ? { ...j, attemptsMade: attempts } : j)));
  };

  const removeJob = (jobId: string) => {
    setJobs((prev = []) => prev.filter((j) => j.jobId !== jobId));
    handlers.current.delete(jobId);
    if (handlers.current.size === 0) {
      setHasRunningJobs(false);
    }
  };

  const startPolling = (job: Job) => {
    setHasRunningJobs(true);
    handlers.current.set(job.jobId, { onResult: onResult });

    (async () => {
      for (let attempt = job.attemptsMade + 1; attempt <= job.maxAttempts; attempt++) {
        try {
          updateAttempts(job.jobId, attempt);

          const { status } = await worksApi.getWorkStatus(job.jobId);

          if (status === WorkStatus.Succeeded) {
            const handler = handlers.current.get(job.jobId);

            if (!handler) {
              console.warn(`No handler found for job ${job.jobId}, removing job`);
            } else {
              const result = await worksApi.getWorkResult(job.jobId);
              await handler.onResult?.(result);
            }

            removeJob(job.jobId);
            return;
          }

          if (status === WorkStatus.Failed || status === WorkStatus.Deleted) {
            console.warn(`Job ${job.jobId} failed or deleted, removing job`);
            OnError?.();
            removeJob(job.jobId);
            return;
          }

          await new Promise((res) => setTimeout(res, job.interval));
        } catch (err) {
          console.error(`Polling error (job ${job.jobId})`, err);
          await new Promise((res) => setTimeout(res, job.interval));
        }
      }

      OnError?.();
      console.warn(`Job ${job.jobId} exceeded max attempts`);
      removeJob(job.jobId);
    })();
  };

  const addJob = (job: Job) => {
    setJobs((prev = []) => {
      const exists = prev.find((j) => j.jobId === job.jobId);
      if (exists) {
        return prev.map((j) => (j.jobId === job.jobId ? job : j));
      } else {
        startPolling(job);
        return [...prev, job];
      }
    });
  };

  // Resume polling for existing jobs
  useEffect(() => {
    if (hasRunningJobs || !jobs || jobs.length === 0) return;

    console.log(`Resuming ${jobs.length} jobs...`);

    jobs.forEach((job) => {
      startPolling(job);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs]);

  return { addJob, isProcessing: hasRunningJobs };
}
